import { type CredentialProvisionType } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Link, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { CreateOrResetCredentialsModal } from "@src/appV2/WorkerCredentials/CreateOrResetCredentialsModal";
import { ManageCredentialsModal } from "@src/appV2/WorkerCredentials/ManageCredentialsModal";
import { useModalState } from "@src/lib/ui-react";

import { ChartingCredentialSoftware } from "../../../WorkerCredentials/api/schema";
import { useChartingCredentialProvisioningConfig } from "../../../WorkerCredentials/api/useCredentialProvisioningConfig";
import { Text } from "../../components/Text";

interface WorkerCredentialProvisionedMessageProps {
  data: string | undefined;
  messageId: number;
  workerId: string;
  workerName: string;
  workplaceId: string;
  sx?: SxProps<Theme>;
}

function parseCredentialProvisionType(data: string): CredentialProvisionType | undefined {
  try {
    const parsed = JSON.parse(data) as { requestType: CredentialProvisionType };
    return parsed.requestType;
  } catch (error) {
    logError("Failed to parse worker credential provisioned message data", {
      error,
      metadata: { data },
    });
    return undefined;
  }
}

/**
 * Renders a worker credential provisioned message with a link to manage credentials.
 * Shows different text based on the provision type (create, update, or reset password).
 * Only renders when charting credential integration is enabled.
 */
export function WorkerCredentialProvisionedMessage(props: WorkerCredentialProvisionedMessageProps) {
  const { data, messageId, workerName, workerId, workplaceId, sx } = props;

  const chartingCredentialIntegrationConfig = useChartingCredentialProvisioningConfig();
  const manageCredentialsModalState = useModalState();
  const createOrResetCredentialsModalState = useModalState();

  if (!chartingCredentialIntegrationConfig.enabled) {
    return null;
  }

  if (!isDefined(data)) {
    logError(APP_V2_APP_EVENTS.WORKER_CREDENTIAL_PROVISIONED_MESSAGE_RENDER_FAILURE, {
      error: "Missing data field on worker credential provisioned message",
      metadata: { messageId },
    });
    return null;
  }

  const requestType = parseCredentialProvisionType(data);
  if (!isDefined(requestType)) {
    logError(APP_V2_APP_EVENTS.WORKER_CREDENTIAL_PROVISIONED_MESSAGE_RENDER_FAILURE, {
      error: "Failed to render WorkerCredentialProvisionedMessage",
      metadata: { messageId },
    });
    return null;
  }

  const software = ChartingCredentialSoftware.POINT_CLICK_CARE;

  const requestTypeToMessage: Record<CredentialProvisionType, string> = {
    // Create and update credentials are treated the same
    create_credentials: `A new ${software} account has been created for ${workerName}.`,
    update_credentials: `A new ${software} account has been created for ${workerName}.`,
    reset_password: `${workerName}'s ${software} account was reset.`,
  };

  const messageText = requestTypeToMessage[requestType];

  const handleViewDetails = () => {
    manageCredentialsModalState.openModal();
  };

  const handleAction = () => {
    manageCredentialsModalState.closeModal();
    createOrResetCredentialsModalState.openModal();
  };

  const handleActionComplete = () => {
    createOrResetCredentialsModalState.closeModal();
    manageCredentialsModalState.openModal();
  };

  return (
    <>
      <Box
        sx={mergeSxProps(
          {
            display: "flex",
            justifyContent: "center",
            padding: 2,
          },
          sx
        )}
      >
        <Text
          variant="caption"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            textAlign: "center",
          })}
        >
          {messageText}{" "}
          <Link
            component="button"
            sx={(theme) => ({
              color: theme.palette.primary.main,
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "inherit",
              fontFamily: "inherit",
              verticalAlign: "baseline",
            })}
            onClick={handleViewDetails}
          >
            View details
          </Link>
        </Text>
      </Box>

      {manageCredentialsModalState.modalIsOpen && (
        <ManageCredentialsModal
          modalState={manageCredentialsModalState}
          workerId={workerId}
          workerName={workerName}
          workplaceId={workplaceId}
          onAction={handleAction}
        />
      )}

      {createOrResetCredentialsModalState.modalIsOpen && (
        <CreateOrResetCredentialsModal
          modalState={createOrResetCredentialsModalState}
          workerId={workerId}
          workerName={workerName}
          workplaceId={workplaceId}
          onAction={handleActionComplete}
        />
      )}
    </>
  );
}
